"use client";

import * as React from "react";
import { Drawer as VaulDrawer } from "vaul";

import { cn } from "./utils";

// Defaults tuned for our app:
//
// • `shouldScaleBackground` → `false` so the page underneath does NOT zoom
//   out when the drawer opens. Vaul's iOS-style scale-back was a recurring
//   complaint.
// • `handleOnly` → `true` so vaul only treats the explicit handle as a drag
//   region. Without this, vaul's pointer handlers attached to the entire
//   Content swallow touchmove events on internal scroll containers,
//   preventing native scrolling. Drawer.Handle keeps drag-to-dismiss working.
const Drawer = ({
  shouldScaleBackground = false,
  handleOnly = true,
  ...props
}: React.ComponentProps<typeof VaulDrawer.Root>) => (
  <VaulDrawer.Root
    shouldScaleBackground={shouldScaleBackground}
    handleOnly={handleOnly}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = VaulDrawer.Trigger;

const DrawerPortal = VaulDrawer.Portal;

const DrawerClose = VaulDrawer.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof VaulDrawer.Overlay>,
  React.ComponentPropsWithoutRef<typeof VaulDrawer.Overlay>
>(({ className, ...props }, ref) => (
  <VaulDrawer.Overlay
    ref={ref}
    className={cn(
      // Apple/iOS native sheet scrim — a soft darkening over the scaled-back
      // page. No blur: the iOS effect comes from the wrapper transform-scale
      // (driven by `data-vaul-drawer-wrapper` on the app shell), not a blur.
      "fixed inset-0 z-50 bg-black/40",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof VaulDrawer.Content>,
  React.ComponentPropsWithoutRef<typeof VaulDrawer.Content> & {
    /**
     * When true, suppresses the built-in sticky grab-handle strip.
     * Use when the caller wants to own the handle position (e.g. floating
     * glass card that needs the handle INSIDE an inset inner wrapper so the
     * entire surface — handle + body — shares one consistent material).
     * The caller MUST render a <DrawerHandle> somewhere inside children so
     * vaul's handleOnly drag logic still has a target element.
     */
    hideInternalHandle?: boolean;
  }
>(({ className, children, hideInternalHandle, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <VaulDrawer.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 rounded-t-[32px] border-x-0 border-b-0 border-t border-glass-border bg-background font-apple",
        "after:absolute after:inset-x-0 after:top-full after:h-[50vh] after:bg-background",
        className
      )}
      {...props}
      // Inline styles to defeat any class-specificity / library-CSS issues:
      // • `touchAction: 'pan-y'` overrides vaul's global `[data-vaul-drawer]
      //   {touch-action:none}`. Without this, no touch gesture can ever pan
      //   inside the drawer (spec: ancestor `none` disables descendants).
      // • The drawer shell itself is NOT the scroll container. Using a flex
      //   column + `overflow: hidden` lets individual sheets provide a real
      //   inner scroll region (`flex-1 min-h-0 overflow-y-auto`) that Vaul
      //   can correctly distinguish from drag gestures.
      // • props.style is spread LAST so callers can override any default
      //   (e.g. pass boxShadow:'none' for a transparent floating shell).
      style={{
        display: "flex",
        flexDirection: "column",
        touchAction: "pan-y",
        maxHeight: "85vh",
        overflow: "hidden",
        boxShadow: "0 -24px 60px rgba(15, 23, 42, 0.18), 0 -6px 18px rgba(15, 23, 42, 0.08)",
        ...(props.style ?? {}),
      }}
    >
      {/* Sticky handle so it stays at the top of the drawer while content
          scrolls. It still drives drag-to-dismiss because it's the only
          element vaul listens to (handleOnly=true on Drawer.Root).
          Suppressed when hideInternalHandle=true — the caller owns the handle. */}
      {!hideInternalHandle && (
        <div className="sticky top-0 z-10 bg-background/98 pt-3 pb-1 flex justify-center backdrop-blur-xl">
          <VaulDrawer.Handle
            className="!m-0 !h-1.5 !w-10 !bg-muted/40 !opacity-100"
          />
        </div>
      )}
      {children}
    </VaulDrawer.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof VaulDrawer.Title>,
  React.ComponentPropsWithoutRef<typeof VaulDrawer.Title>
>(({ className, ...props }, ref) => (
  <VaulDrawer.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight font-apple text-foreground",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof VaulDrawer.Description>,
  React.ComponentPropsWithoutRef<typeof VaulDrawer.Description>
>(({ className, ...props }, ref) => (
  <VaulDrawer.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground font-apple", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

/** Vaul drag handle — place inside DrawerContent children when using hideInternalHandle. */
export const DrawerHandle = VaulDrawer.Handle;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
